import { Button } from '../../components'
import { CreateCollectionCommand } from '@indigohive/cogfy-types/endpoints/createCollection'
import { useCogfy, useCollection, useToasts, useWorkspaceSlug } from '../../hooks'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { UUID } from '@indigohive/cogfy-types'
import clsx from 'clsx'
import { ImagesGallery, TemplateDescription } from '@indigohive/templates-gallery-components'
import { MAP_LOGO_BY_PROVIDER } from '../../components/__common/map-logo-by-provider'
import { Provider } from '@indigohive/cogfy-providers'

export function CollectionTemplatePage () {
  const cogfy = useCogfy()
  const { t } = useTranslation()
  const workspace = useWorkspaceSlug()
  const navigate = useNavigate()
  const toasts = useToasts()
  const params = useParams<{ templateSlug: string }>()
  const [searchParams] = useSearchParams()
  const parent = useCollection(searchParams.get('folder') as UUID | undefined)
  const slug = params.templateSlug!

  const createCollection = useMutation({
    mutationFn: (data: CreateCollectionCommand) => cogfy.createCollection(data),
    onSuccess: result => {
      navigate(`/${workspace}/${result.id}`)
      toasts.success(t('collectionTemplatesPage:Collection created'))
    },
    onError: () => {
      toasts.error(t('collectionTemplatesPage:Error creating collection'))
    }
  })

  const getTemplate = useQuery({
    queryKey: ['getCollectionTemplateBySlug', { slug }] as const,
    queryFn: ({ queryKey, signal }) => cogfy.getCollectionTemplateBySlug(queryKey[1], { signal }),
    enabled: slug !== undefined
  })

  const files = getTemplate.data?.files
  const hasFiles = files && files.length > 0
  const name = getTemplate.data?.name
  const description = getTemplate.data?.description

  return (
    <div className="flex flex-col justify-between gap-8 h-full">
      <div className={clsx('p-14 flex items-center gap-8', !hasFiles && 'min-h-[40vh]')}>
        <div className={clsx('text-left h-fit basis-5/12', !hasFiles && 'max-w-screen-md mx-auto')}>
          <h1 className="text-3xl font-bold mb-1">{name}</h1>
          <p className="text-base opacity-30 line-clamp-6 mb-4">{description}</p>
          <Button
            disabled={createCollection.isPending}
            onClick={() => createCollection.mutate({
              parentId: parent.data?.id,
              templateId: getTemplate.data?.id
            })}
            className="font-bold text-white bg-black p-3 rounded-xl hover:bg-black/45"
          >
            {t('Use template')}
          </Button>
        </div>

        <ImagesGallery images={files?.map(file => file.url) ?? []}/>
      </div>
      {getTemplate.data &&
        <TemplateDescription
          template={getTemplate.data}
          providers={
            getTemplate.data.providers
              ?.map(provider => (
                {
                  name: provider.label as Provider['name'],
                  image: MAP_LOGO_BY_PROVIDER[provider.name] ?? ''
                }
              ))}
        />
      }
    </div>
  )
}
